import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import ComboBox from 'Components/ComboBox'
import CustomButton from 'Components/CustomButton/CustomButton'
import { CustomTextField } from 'Components/CustomTextField'

import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { makePostRequest } from 'utils/api'

const acc_types = [
  { id: 1, value: 'Google', label: 'Google', categorie: 'Google' },
  { id: 2, value: 'Microsoft', label: 'Microsoft', categorie: 'Microsoft' },
  { id: 3, value: 'iCloud', label: 'iCloud', categorie: 'iCloud' },
]

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function AddConnection({
  updateList,
  openM = false,
  initialType = null, // ex. 'iCloud' — pré-sélectionne le type (reconnexion)
  showTrigger = true, // false = pas de bouton « créer », dialog piloté par le parent
  onClose = null,
}) {
  const initialTypeObj = initialType
    ? acc_types.find((t) => t.value === initialType) || {}
    : {}
  const [open, setOpen] = useState(openM)
  const [type, setType] = useState(initialTypeObj)

  // --- État spécifique iCloud (connexion par CardDAV, sans OAuth) -----------
  const [appleId, setAppleId] = useState('')
  const [appPassword, setAppPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const navigate = useNavigate()
  const location = useLocation()
  const w_contact_import = localStorage.getItem('w-contact-import')

  const isICloud = type?.value === 'iCloud'

  const resetState = () => {
    setType(initialTypeObj)
    setAppleId('')
    setAppPassword('')
    setShowPassword(false)
    setError('')
    setLoading(false)
  }

  const handleClickOpen = () => setOpen(true)

  const handleClose = () => {
    setOpen(false)
    resetState()
    updateList()
    if (onClose) onClose()
  }

  const handleCancel = () => {
    handleClose()
    if (w_contact_import) {
      localStorage.removeItem('w-contact-import')
      navigate('/')
    }
  }

  // --- Google / Microsoft : flux OAuth (redirection) -----------------------
  const handleOAuthConnect = async () => {
    const payload = { type: type.label, categorie: type.categorie }
    const response = await makePostRequest('/api/user/add-connectes/', payload, {}, true)
    if (response && response.status === 200) {
      localStorage.setItem('return_url', location.pathname)
      localStorage.setItem('opencc', true)
      window.location.replace(response.data.url)
      handleClose()
    } else {
      setError("La connexion au compte a échoué. Veuillez réessayer.")
    }
  }

  // --- iCloud : connexion CardDAV via mot de passe pour application --------
  const handleICloudConnect = async () => {
    const id = appleId.trim()
    const pwd = appPassword.trim()

    if (!EMAIL_RE.test(id)) {
      setError("Veuillez saisir un identifiant Apple valide (adresse e-mail).")
      return
    }
    if (!pwd) {
      setError('Veuillez saisir votre mot de passe pour application.')
      return
    }

    setLoading(true)
    setError('')
    const response = await makePostRequest(
      '/api/user/connect-icloud/',
      { apple_id: id, app_password: pwd },
      {},
      true,
    )
    setLoading(false)

    if (response && response.status === 200 && response.data?.success) {
      // On informe l'utilisateur du résultat réel de l'import au lieu de
      // fermer en silence (un 0 contact passait inaperçu).
      if (response.data?.warning) {
        alert(response.data.warning)
      } else {
        const nb = response.data?.i_contacts ?? 0
        alert(
          nb > 0
            ? `Compte iCloud connecté : ${nb} contact(s) importé(s).`
            : 'Compte iCloud connecté. Aucun nouveau contact à importer.',
        )
      }
      handleClose()
      return
    }

    // Message d'erreur renvoyé par le backend, sinon message générique.
    const backendError = response?.data?.error
    if (response?.status === 401) {
      setError(
        backendError ||
          "Identifiants iCloud refusés. Vérifiez votre identifiant Apple et " +
            "utilisez bien un mot de passe pour application (et non votre mot " +
            'de passe Apple habituel).',
      )
    } else {
      setError(
        backendError || "Connexion à iCloud impossible. Veuillez réessayer.",
      )
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    if (!type?.value) {
      setError('Veuillez choisir un type de compte.')
      return
    }
    if (isICloud) {
      handleICloudConnect()
    } else {
      handleOAuthConnect()
    }
  }

  return (
    <>
      {showTrigger && <CustomButton onClick={handleClickOpen}>créer</CustomButton>}
      <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
        <DialogTitle sx={{ color: 'black !important' }}>Connecter un compte</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <ComboBox
              handleAutoComplete={(e, j) => {
                setType(j || {})
                setError('')
                setAppleId('')
                setAppPassword('')
              }}
              label="Accounts"
              data={acc_types}
              name="type"
              selectedValue={type?.value ? type : null}
              required={true}
            />

            {/* ----- Formulaire iCloud (affiché uniquement si iCloud) ----- */}
            {isICloud && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    iCloud ne propose pas de connexion en un clic. Vous devez
                    créer un <strong>mot de passe pour application</strong> :
                  </Typography>
                  <Typography variant="body2" component="div">
                    1. Ouvrez{' '}
                    <Link
                      href="https://account.apple.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      account.apple.com
                    </Link>
                    <br />
                    2. Rubrique « Connexion et sécurité » → « Mots de passe
                    pour application »
                    <br />
                    3. Créez-en un et copiez-le ci-dessous
                  </Typography>
                </Alert>

                <CustomTextField
                  label="Identifiant Apple"
                  type="email"
                  placeholder="prenom@icloud.com"
                  value={appleId}
                  onChange={(e) => setAppleId(e.target.value)}
                  fullWidth
                  required
                />

                <CustomTextField
                  label="Mot de passe pour application"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="xxxx-xxxx-xxxx-xxxx"
                  value={appPassword}
                  onChange={(e) => setAppPassword(e.target.value)}
                  fullWidth
                  required
                />

                <Button
                  size="small"
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                >
                  {showPassword ? 'Masquer' : 'Afficher'} le mot de passe
                </Button>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {error}
              </Alert>
            )}
          </DialogContent>

          <DialogActions>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <CircularProgress size={18} sx={{ mr: 1 }} />
              ) : null}
              {isICloud ? 'Connecter' : 'Valider'}
            </Button>
            <Button type="button" onClick={handleCancel} disabled={loading}>
              Annuler
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
